import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Aviso, Botao, CampoDeSelecao, IconeLixeira, useDialogo } from '../../componentes/ui';
import { lerComoBase64 } from '../../servicos/arquivo';
import { baixarArquivo, requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';

interface DocumentoDoContrato {
  readonly id: string;
  /** `CONTRATO` (gerado pelo Deca) ou `CONTRATO_ASSINADO` (anexado). */
  readonly tipo: string;
  readonly nome: string;
  readonly geradoPeloSistema: boolean;
  readonly enviadoParaAssinatura: boolean;
  readonly criadoEm: string;
}

interface ContratoEmPdfDoPassivo {
  readonly modelos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly origem: 'FORNECEDOR' | 'ASSESSOR' | 'GERAL';
    readonly nomeDaEmpresa: string | null;
  }[];
  readonly escolhido: string | null;
  readonly enviadoEm: string | null;
  readonly documentos: readonly DocumentoDoContrato[];
}

/**
 * O cartão "Contrato" do Passivo (D-020, etapa 4): escolher o modelo, gerar o
 * PDF, mandar ao D4Sign e guardar a via assinada.
 *
 * Funciona desde a simulação, e "Ativar contrato" não depende dele. Assinam o
 * cliente, o fornecedor e a MS3 — cada uma pelo colaborador marcado "assina
 * pela empresa".
 */
export function ContratoEmPdf(props: {
  readonly contratoId: string;
  readonly cancelado: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const chave = ['passivo-contrato-pdf', props.contratoId];
  const base = `/passivo/contratos/${props.contratoId}/contrato-pdf`;
  /** `null` até mexer: vale o escolhido do servidor. `''` = esqueleto padrão. */
  const [modelo, setModelo] = useState<string | null>(null);
  const [assinado, setAssinado] = useState<File | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  const estado = useQuery({
    queryKey: chave,
    queryFn: () => requisicaoAutenticada<ContratoEmPdfDoPassivo>(base),
  });
  const atualizar = (novo: ContratoEmPdfDoPassivo): void => {
    queryClient.setQueryData(chave, novo);
  };

  const gerar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<ContratoEmPdfDoPassivo>(base, {
        metodo: 'POST',
        corpo: { modeloId: (modelo ?? estado.data?.escolhido ?? '') || null },
      }),
    onSuccess: atualizar,
  });
  const enviar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<ContratoEmPdfDoPassivo>(`${base}/envio`, { metodo: 'POST', corpo: {} }),
    onSuccess: atualizar,
  });
  const anexar = useMutation({
    mutationFn: async (arquivo: File) =>
      requisicaoAutenticada<ContratoEmPdfDoPassivo>(`${base}/assinado`, {
        metodo: 'POST',
        corpo: {
          nome: arquivo.name,
          conteudoBase64: await lerComoBase64(arquivo),
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
        },
      }),
    onSuccess: (novo) => {
      atualizar(novo);
      setAssinado(null);
    },
  });
  const remover = useMutation({
    mutationFn: (documentoId: string) =>
      requisicaoAutenticada<void>(`/passivo/documentos/${documentoId}`, { metodo: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: chave }),
  });

  useEffect(() => {
    if (assinado === null && campo.current !== null) campo.current.value = '';
  }, [assinado]);

  if (estado.isError) {
    return <ErroDaApi titulo="Não foi possível carregar o contrato" erro={estado.error} />;
  }
  const dados = estado.data;
  const gerados = dados?.documentos.filter((d) => d.tipo === 'CONTRATO') ?? [];
  const ultimoGerado = gerados.at(-1) ?? null;
  const escolhido = modelo ?? dados?.escolhido ?? '';
  const erro = gerar.error ?? enviar.error ?? anexar.error ?? remover.error;

  return (
    <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-conteudo-primario">Contrato</h3>
        {dados?.enviadoEm !== null && dados?.enviadoEm !== undefined && (
          <span className="text-xs text-conteudo-secundario">
            Enviado para assinatura em {new Date(dados.enviadoEm).toLocaleDateString('pt-BR')}
          </span>
        )}
      </div>
      <p className="text-xs text-conteudo-secundario">
        O contrato sai dos dados desta operação, no modelo escolhido. Assinam o cliente, o
        fornecedor e a MS3. Pode gerar ainda na simulação.
      </p>

      {!props.cancelado && (
        <div className="flex flex-wrap items-end gap-2">
          <CampoDeSelecao
            rotulo="Modelo do contrato"
            className="w-full sm:w-96"
            value={escolhido}
            onChange={(e) => setModelo(e.target.value)}
            opcoes={[
              ...(dados?.modelos ?? []).map((m) => ({
                valor: m.id,
                rotulo: `${m.nome} — ${m.origem === 'GERAL' ? 'geral' : 'do fornecedor'}`,
              })),
              { valor: '', rotulo: 'Esqueleto padrão do Deca' },
            ]}
          />
          <Botao
            variante="secundario"
            disabled={dados === undefined}
            carregando={gerar.isPending}
            onClick={() => gerar.mutate()}
          >
            {gerados.length > 0 ? 'Gerar de novo' : 'Gerar contrato'}
          </Botao>
          <Botao
            variante="secundario"
            disabled={ultimoGerado === null || ultimoGerado.enviadoParaAssinatura}
            carregando={enviar.isPending}
            onClick={() => {
              void (async () => {
                const ok = await confirmar({
                  titulo: 'Enviar o contrato para assinatura?',
                  descricao:
                    'O último contrato gerado vai para o D4Sign, que envia e-mail para quem assina pelo cliente, pelo fornecedor e pela MS3.',
                  rotuloConfirmar: 'Enviar',
                });
                if (ok) enviar.mutate();
              })();
            }}
          >
            Enviar para assinatura
          </Botao>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-conteudo-primario">Via assinada:</span>
        <input
          ref={campo}
          type="file"
          aria-label="Arquivo do contrato assinado"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          onChange={(e) => setAssinado(e.target.files?.[0] ?? null)}
          className="min-w-0 max-w-full text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
        />
        <Botao
          variante="secundario"
          disabled={assinado === null}
          carregando={anexar.isPending}
          onClick={() => {
            if (assinado !== null) anexar.mutate(assinado);
          }}
        >
          Anexar
        </Botao>
      </div>

      {(dados?.documentos.length ?? 0) > 0 && (
        <ul className="flex flex-col divide-y divide-borda rounded-card border border-borda">
          {dados!.documentos.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="flex min-w-0 flex-col">
                <button
                  type="button"
                  onClick={() => void baixarArquivo(`/passivo/documentos/${d.id}`, d.nome)}
                  className="truncate text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
                >
                  {d.nome}
                </button>
                <span className="text-xs text-conteudo-sutil">
                  {d.tipo === 'CONTRATO' ? 'Gerado pelo Deca' : 'Via assinada'} em{' '}
                  {new Date(d.criadoEm).toLocaleDateString('pt-BR')}
                  {d.enviadoParaAssinatura && ' · enviado para assinatura'}
                </span>
              </span>
              <button
                type="button"
                aria-label={`Remover ${d.nome}`}
                title="Remover documento"
                className="rounded-card p-1 text-conteudo-sutil transition-colors hover:bg-erro/10 hover:text-erro"
                onClick={() => {
                  void (async () => {
                    const ok = await confirmar({
                      titulo: 'Remover este documento?',
                      descricao: 'Ele sai do contrato. O que já foi enviado ao D4Sign não muda.',
                      rotuloConfirmar: 'Remover',
                      tom: 'perigo',
                    });
                    if (ok) remover.mutate(d.id);
                  })();
                }}
              >
                <IconeLixeira className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {anexar.isSuccess && (
        <Aviso tom="positivo" titulo="Via assinada guardada">
          O arquivo ficou no contrato.
        </Aviso>
      )}
      {erro !== null && <ErroDaApi titulo="Não foi possível" erro={erro} />}
    </section>
  );
}
